import axios from "axios";
import moment from "moment";
import enviroment from "../enviroments";

// Constantes
export const TOKEN_KEY = "@token";
export const TOKEN_EXPIRATION_KEY = "@token-expires-in";
const REFRESH_CHECK_MARGIN_MINUTES = 5; // Renova se faltar menos de 5 minutos para expirar
const GLOBAL_MIN_REFRESH_INTERVAL = 30 * 1000; // 30 segundos entre refreshes
const MAX_REFRESH_RETRIES = 2; // Número máximo de tentativas de refresh sem sucesso

// Variáveis de controle global
let globalRefreshInProgress = false;
let lastSuccessfulRefresh = 0;
let refreshRetryCount = 0;

/**
 * Verifica se o token atual precisa ser renovado com base no tempo restante
 * @returns boolean - true se precisa renovar, false caso contrário
 */
export function needsTokenRefresh(): boolean {
  try {
    // Verificar se existe token
    const tokenString = localStorage.getItem(TOKEN_KEY);
    if (!tokenString) return true;

    // Verificar data de expiração
    const expirationString = localStorage.getItem(TOKEN_EXPIRATION_KEY);
    if (!expirationString) return true;

    // Calcular tempo restante até expiração
    const expirationDate = JSON.parse(expirationString);
    const minutesLeft = moment(expirationDate).diff(moment(), "minutes");

    // Renovar se faltar menos de X minutos
    return minutesLeft < REFRESH_CHECK_MARGIN_MINUTES;
  } catch (error) {
    console.error("[useRefreshToken] Erro ao verificar expiração:", error);
    return true; // Em caso de erro, melhor tentar renovar
  }
}

/**
 * Tenta renovar o token usando o refresh token armazenado
 * @returns Promise<boolean> - true se o refresh foi bem-sucedido, false caso contrário
 */
export async function handleRefreshToken(): Promise<boolean> {
  // Evitar chamadas duplicadas
  if (globalRefreshInProgress) {
    console.log("[useRefreshToken] Refresh já em andamento, ignorando chamada");
    return true; // Retorna true para evitar logout desnecessário
  }

  // Evitar chamadas muito frequentes
  const now = Date.now();
  if (now - lastSuccessfulRefresh < GLOBAL_MIN_REFRESH_INTERVAL) {
    console.log("[useRefreshToken] Refresh recente, ignorando chamada");

    // Verificar se o token existe e é válido
    const tokenString = localStorage.getItem(TOKEN_KEY);
    if (tokenString) {
      try {
        const parsedToken = JSON.parse(tokenString);
        if (parsedToken?.token) {
          return true; // Token existe, consideramos válido
        }
      } catch (e) {
        console.error("[useRefreshToken] Erro ao analisar token:", e);
      }
    }

    // Se chegou aqui, não temos um token válido mesmo após refresh recente
    return false;
  }

  try {
    // Marcar início do processo
    globalRefreshInProgress = true;
    console.log("[useRefreshToken] Iniciando refresh token");

    // Obter refresh token do storage
    const tokenString = localStorage.getItem(TOKEN_KEY);
    if (!tokenString) {
      console.log("[useRefreshToken] Token não encontrado");
      refreshRetryCount += 1; // Incrementar contagem de falhas (evitando ++)
      return false;
    }

    const parsedToken = JSON.parse(tokenString);
    if (!parsedToken?.refreshToken) {
      console.log("[useRefreshToken] Refresh token não encontrado");
      refreshRetryCount += 1; // Incrementar contagem de falhas (evitando ++)
      return false;
    }

    // Se tivemos muitas falhas seguidas, desistir
    if (refreshRetryCount >= MAX_REFRESH_RETRIES) {
      console.log(
        `[useRefreshToken] Muitas tentativas falhas (${refreshRetryCount}), desistindo`
      );
      refreshRetryCount = 0; // Resetar contador para não prender o sistema
      return false;
    }

    // Criar instância isolada do axios para evitar loops
    const axiosInstance = axios.create({
      baseURL: enviroment.BASE_URL,
      headers: { "Content-Type": "application/json" },
      params: { cc: "6909b8b9-7dfc-40e2-af74-3d0b25c65248" },
      timeout: 10000, // Timeout de 10 segundos
    });

    // Log de tentativa
    console.log("[useRefreshToken] Chamando API para refresh token");

    // Chamar endpoint de refresh token
    const response = await axiosInstance.get(
      `identity/conta-usuario/v1/refresh-token?refreshToken=${parsedToken.refreshToken}`
    );

    // Verificar resposta
    if (response?.status === 200 && response?.data?.token) {
      // Atualizar tokens no localStorage
      const newToken = {
        token: response.data.token.access_token,
        refreshToken: response.data.token.refresh_token,
        expires_in: response.data.token.expires_in,
        tokenAD: parsedToken.tokenAD, // Manter o token AD original
      };

      // Calcular nova data de expiração
      const expirationDate = new Date();
      const expiresInSeconds = parseInt(
        newToken.expires_in?.substring(0, newToken.expires_in.length - 1),
        10
      );
      expirationDate.setSeconds(expirationDate.getSeconds() + expiresInSeconds);

      // Salvar no localStorage
      localStorage.setItem(TOKEN_KEY, JSON.stringify(newToken));
      localStorage.setItem(
        TOKEN_EXPIRATION_KEY,
        JSON.stringify(expirationDate)
      );

      // Atualizar controle de refresh
      lastSuccessfulRefresh = Date.now();
      refreshRetryCount = 0; // Resetar contador de falhas
      console.log("[useRefreshToken] Token renovado com sucesso");

      return true;
    }

    console.log("[useRefreshToken] Falha na renovação do token");
    refreshRetryCount += 1; // Incrementar contagem de falhas (evitando ++)
    return false;
  } catch (error) {
    console.error("[useRefreshToken] Erro ao renovar token:", error);
    refreshRetryCount += 1; // Incrementar contagem de falhas (evitando ++)
    return false;
  } finally {
    // Importante: garantir que a flag seja limpa
    globalRefreshInProgress = false;
  }
}

/**
 * Verifica e renova o token se necessário
 * @returns Promise<boolean> - true se o token está válido ou foi renovado com sucesso
 */
export async function checkAndRefreshTokenIfNeeded(): Promise<boolean> {
  if (needsTokenRefresh()) {
    console.log(
      "[useRefreshToken] Token precisa ser renovado, iniciando refresh"
    );
    return handleRefreshToken();
  }
  return true; // Token ainda válido
}

/**
 * Limpa todos os dados de autenticação do localStorage
 */
export function clearAuthData(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRATION_KEY);
  // Reset das variáveis globais de controle
  refreshRetryCount = 0;
  lastSuccessfulRefresh = 0;
}

/**
 * Obtém o token atual do localStorage
 * @returns string | null - token atual ou null se não existir
 */
export function getCurrentToken(): string | null {
  try {
    const tokenString = localStorage.getItem(TOKEN_KEY);
    if (!tokenString) return null;

    const parsedToken = JSON.parse(tokenString);
    return parsedToken?.token || null;
  } catch (error) {
    console.error("[useRefreshToken] Erro ao obter token atual:", error);
    return null;
  }
}

/**
 * Hook para disponibilizar funções de refresh token
 */
export default function useRefreshToken() {
  return {
    needsTokenRefresh,
    handleRefreshToken,
    checkAndRefreshTokenIfNeeded,
    clearAuthData,
    getCurrentToken,
  };
}
